import { useEffect, useState } from "react";

// Fieldworker mode: the signed-in user joined a MewMaps project that is SHARED with
// them (owned by another, "manager" account) rather than created here. In this mode the
// app must never bootstrap new project resources (root folder, spreadsheet, config tabs,
// assets) and the UI is narrowed to Field entry + Map so fieldworkers stay focused on
// data collection. It is a workflow restriction, not a security boundary — the manager
// shares the folder as Editor, so full write access is intentional.

const KEY = "mew.fieldworker";
const EVENT = "mew.fieldworker.change";

// Marks the current project as one JOINED from another account (the spreadsheet is not
// ownedByMe), independent of tier. Both non-owner managers and fieldworkers are "shared".
// Under drive.file the joined root's existing children aren't listable and can't be created
// into, so resource resolution must Pick shared subfolders rather than search/create them.
const SHARED_KEY = "mew.shared_project";

export function isSharedProject(): boolean {
  try {
    return localStorage.getItem(SHARED_KEY) === "1";
  } catch {
    return false;
  }
}

export function setSharedProject(on: boolean): void {
  try {
    if (on) localStorage.setItem(SHARED_KEY, "1");
    else localStorage.removeItem(SHARED_KEY);
  } catch {}
}

export function isFieldworkerMode(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function setFieldworkerMode(on: boolean): void {
  try {
    if (on) localStorage.setItem(KEY, "1");
    else localStorage.removeItem(KEY);
  } catch {}
  window.dispatchEvent(new CustomEvent(EVENT, { detail: !!on }));
}

export function useFieldworkerMode(): boolean {
  const [on, setOn] = useState(isFieldworkerMode);
  useEffect(() => {
    const handler = () => setOn(isFieldworkerMode());
    window.addEventListener(EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);
  return on;
}
